import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import { Popover, PopoverBody } from "reactstrap";
import i18n from 'i18next';
import AttachmentPreview from './AttachmentPreview';
import {getCredentials} from '../../selectors/application';
import {downloadAttachment} from '../../services/message';
import Spinner from '../spinner/spinner';
import './attachment-card.scss';
import OptionsIcon from '../../assets/icons/icon-options-vertical.svg?react';
import DeleteIcon from '../../assets/icons/delete.svg?react';

export class AttachmentCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      downloading: false,
      dropdownOpen: false,
      showPreview: false,
      isHovered: false
    };
    this.optionsIconContainerRef = React.createRef();
    this.cardRef = React.createRef();
    this.tooltipRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleOutsideClick);
    window.addEventListener('blur', this.handleWindowBlur);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleOutsideClick);
    window.removeEventListener('blur', this.handleWindowBlur);
    if (this.tooltipObj) {
      try {
        this.tooltipObj.close(); // O destroy() si existe
      } catch (e) {
        console.error("Error closing tooltip:", e);
      }
    }
  }

  handleOutsideClick = event => {
    if (!this.state.dropdownOpen) {
      return;
    }

    if (this.optionsIconContainerRef.current && this.optionsIconContainerRef.current.contains(event.target)) {
      return;
    }

    const popoverNode = document.querySelector('.popover-attachment.show');
    if (popoverNode && popoverNode.contains(event.target)) {
      return;
    }

    this.setState({ dropdownOpen: false });
  };

  handleWindowBlur = () => {
    if (this.state.dropdownOpen) {
      this.setState({ dropdownOpen: false });
    }
  };

  addAttachmentImg = (fileName = "") => {
    fileName = fileName || "";
    const fileExtension = fileName.toLowerCase().split('.').pop();

    let imgPath;

    switch (fileExtension) {
      case 'png':
      case 'gif':
      case 'bmp':
      case 'svg':
      case 'jpg':
      case 'jpeg':
        imgPath = "/assets/images/icon-microsoft-img.svg";
        break;

      case 'pdf':
        imgPath = "/assets/images/icon-microsoft-pdf.svg";
        break;

      case 'doc':
      case 'docx':
        imgPath = "/assets/images/icon-microsoft-word.svg";
        break;

      case 'csv':
      case 'xlsx':
      case 'xls':
        imgPath = "/assets/images/icon-microsoft-excel.svg";
        break;

      case 'ppt':
      case 'pptx':
        imgPath = "/assets/images/icon-microsoft-ppt.svg";
        break;

      // Media formats
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'mkv':
      case 'wmv':
      case 'flv':
      case 'mp3':
      case 'wav':
      case 'ogg':
      case 'flac':
        imgPath = "assets/images/icon-media.svg";
        break;

      // Compressed formats
      case 'zip':
        imgPath = "/assets/images/icon-zip.svg";
        break;

      case 'rar':
      case 'tar':
      case 'gz':
      case '7z':
        imgPath = "/assets/images/icon-compressed.svg";
        break;

      case 'html':
      case 'htm':
        imgPath = "/assets/images/icon-html.svg";
        break;

      default:
        imgPath = "/assets/images/icon-doc.svg";
        break;
    }

    return imgPath;
  };

  openPreview = () => {
    this.setState({
      showPreview: true,
      dropdownOpen: false
    });
  };

  handleOptionsClick = e => {
    e.stopPropagation();
    this.setState(prevState => ({
      dropdownOpen: !prevState.dropdownOpen
    }));
  };

  handleCardClick = () => {
    if (this.props.variant === 'viewer' || this.props.variant === 'compose') {
      this.openPreview();
    } else {
      return;
    }
  };

  render() {
    const {i, attachment, variant, onRemove } = this.props;
    const { dropdownOpen, isHovered } = this.state;

    return (
      <React.Fragment>
        <span
          ref={this.cardRef}
          id={`attachment-document-${i}`}
          className="message-content-attachment-container"
          onClick={this.handleCardClick}
          onMouseEnter={() => {
            this.setState({ isHovered: true });
            // Syncfusion TooltipComponent methods might not be available immediately,
            // so we use setTimeout to ensure it is ready
            setTimeout(() => {
              if (this.tooltipRef.current) {
                this.tooltipRef.current.open(document.getElementById(`attachment-document-${i}`));
              }
            }, 0);
          }}
          onMouseLeave={() => {
            this.setState({ isHovered: false });
            // Syncfusion TooltipComponent methods might not be available immediately,
            // so we use setTimeout to ensure it is ready
            setTimeout(() => {
              if (this.tooltipRef.current) {
                this.tooltipRef.current.close();
              }
            }, 0);
          }}
        >
          <Spinner className={'spinner'} canvasClassName={'canvas'} visible={this.state.downloading}/>
          <div className='d-flex'>
            <img
              src={this.addAttachmentImg(attachment.fileName)}
              alt=""
              width={18}
              height={18}
            />
            <span className="message-content-attachment-name">
              {attachment.fileName ?
                attachment.fileName :
                `documento.${
                  attachment.contentType ?
                    attachment.contentType.split('/').pop()
                    : 'dat'}`}
            </span>
            <span className="message-content-attachment-size">
              {(attachment.size / 1000).toFixed(2).replace(/\./g, ",")} KB
            </span>
            {(variant === 'viewer' || variant === 'compose') && (
              <div
                ref={this.optionsIconContainerRef}
                className={"attachment-options"}
                onClick={this.handleOptionsClick}
              >
                {isHovered && <OptionsIcon />}
              </div>
            )}
          </div>
        </span>
        <Popover
          isOpen={dropdownOpen}
          target={`attachment-document-${i}`}
          toggle={() => {
            this.setState({dropdownOpen: false});
          }}
          placement="bottom-end"
          className="popover-attachment"
        >
          <PopoverBody>
            <div className="popover-attachment-container">
              {/* Preview option */}
              <div
                className="d-flex align-items-center popover-attachment-options py-2 px-1"
                onClick={() => {
                  this.openPreview();
                }}
              >
                <img
                  src={`/assets/images/icon-show.svg`}
                  alt={i18n.t('attachment_preview.preview')}
                  width="17px"
                  height="17px"
                />
                <span className="ml-2 text-color-grey-800">{i18n.t('attachment_preview.preview')}</span>
              </div>

              {/* Download option */}
              {variant === 'viewer' && (
                <div
                  className="d-flex align-items-center popover-attachment-options py-2 px-1"
                  onClick={() => {
                    if (attachment.content) {
                      this.localDownload(attachment);
                    } else {
                      this.download(attachment);
                    }
                    this.setState({dropdownOpen: false});
                  }}
                >
                  <img
                    src={`/assets/images/download.svg`}
                    alt={i18n.t('general.download')}
                    width="17px"
                    height="17px"
                  />
                  <span className="ml-2 text-color-grey-800">{i18n.t('general.download')}</span>
                </div>
              )}

              {variant === 'compose' && (
                <div
                  className="d-flex align-items-center popover-attachment-options py-2 px-1"
                  onClick={() => {
                    if (onRemove) {
                      onRemove();
                    }
                    this.setState({dropdownOpen: false});
                  }}
                >
                  <DeleteIcon width="17px" height="17px" />
                  <span className="ml-2 text-color-grey-800">{i18n.t('general.remove')}</span>
                </div>
              )}
            </div>
          </PopoverBody>
        </Popover>
        {this.state.showPreview && (
          <AttachmentPreview
            attachment={attachment}
            onClose={() => this.setState({ showPreview: false })}
            onDownload={() => {
              if (attachment.content) {
                this.localDownload(attachment);
              } else {
                this.download(attachment);
              }
            }}
          />
        )}
      </React.Fragment>
    );
  }

  localDownload(attachment) {
    if (this.state.downloading) {
      return;
    }
    this.setState({ downloading: true });

    const byteCharacters = atob(attachment.content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    const blob = new Blob(
      [byteArray],
      {
        type: attachment.contentType || 'application/octet-stream'
      });

    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = attachment.fileName || `documento.${
      attachment.contentType ?
        attachment.contentType.split('/').pop()
        : 'dat'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);

    this.setState({ downloading: false });
  }

  download(attachment) {
    if (this.state.downloading) {
      return;
    }

    this.setState({
      downloading: true
    });

    const ieCompatibleFinally = () =>
      this.setState({
        downloading: false
      });

    downloadAttachment(this.props.credentials, attachment)
      .then(ieCompatibleFinally)
      .catch(ieCompatibleFinally);
  }
}

AttachmentCard.propTypes = {
  attachment: PropTypes.object,
  i: PropTypes.number.isRequired
};

const mapStateToProps = state => ({
  credentials: getCredentials(state)
});

export default connect(mapStateToProps)(AttachmentCard);
